import { CommonModule, DOCUMENT } from '@angular/common';
import {
  type AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  type ElementRef,
  type OnChanges,
  type SimpleChanges,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { fromEvent, interval } from 'rxjs';
import { DomSanitizer, type SafeUrl } from '@angular/platform-browser';
import { CdkTextareaAutosize, TextFieldModule } from '@angular/cdk/text-field';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { LaboratoryAttachmentChipComponent } from '@skyeng/libs/laboratory/ui';
import { provideTranslationStatic } from '@skyeng/libs/shared/i18n';
import { DsIconModule } from '@skyeng/libs/shared/ui-kit-v2/icon';
import { DsSpinnerModule } from '@skyeng/libs/shared/ui-kit-v2/spinner';

import { type ReportChatMessage, type ReportChatMessageFile, type ReportChatViewerRole } from '../../interfaces';
import { type ReportChatBodySegment, splitBodyIntoSegments } from '../../linkify';
import { messageDay, messageTimeFormat } from '../../message-time-format';
import deTranslation from './translation/de.json';
import enTranslation from './translation/en.json';
import esTranslation from './translation/es.json';
import frTranslation from './translation/fr.json';
import itTranslation from './translation/it.json';
import jaTranslation from './translation/ja.json';
import koTranslation from './translation/ko.json';
import plTranslation from './translation/pl.json';
import ptTranslation from './translation/pt.json';
import ruTranslation from './translation/ru.json';
import ukTranslation from './translation/uk.json';
import { LABORATORY_REPORT_CHAT_TRANSLATION_SCHEME } from './translation/translation-schema';

/** Hard limit mirrored from the proxy `MESSAGE_MAX_LENGTH`. */
const MESSAGE_MAX_LENGTH = 5000;

/**
 * Pixels of slack at the bottom within which we still treat the user as
 * "pinned to bottom" and may auto-scroll new messages into view. Above this
 * gap the user is reading history, so we surface the "new messages" pill
 * instead of yanking the viewport (scroll-jacking guard).
 */
const PIN_TO_BOTTOM_SLACK_PX = 80;
/** Tolerance (px) when deciding a message is fully scrolled into view for the
 * read cursor — absorbs sub-pixel layout so the last visible row still counts. */
const VIEWED_SLACK_PX = 4;

interface ChatRow {
  kind: 'message' | 'divider' | 'day';
  /** Stable track key. */
  key: string;
  /** Present when `kind === 'message'`. */
  message?: ReportChatMessage;
  /**
   * {@link ReportChatMessage.body} pre-split into text and link runs. Computed
   * here rather than called from the template so the parse happens once per
   * message change, not on every change-detection pass.
   */
  bodySegments?: ReportChatBodySegment[];
  timeFormat?: string | null;
  dayDate?: string;
  dayLabel?: string;
  dayLabelKey?: string;
}

/** Composer draft attachment chip (pre-send). Presentational; upload lives in the host. */
export interface ReportChatDraftAttachment {
  label: string;
  /**
   * Object/asset URL of the image being attached. When set, the chip renders
   * a clickable thumbnail that opens the fullscreen lightbox — so the user
   * can verify the screenshot/photo before sending. Non-image attachments
   * omit it and keep the plain paperclip chip.
   */
  previewUrl?: string;
}

/**
 * Shared per-report support-chat thread, consumed by both the student modal
 * and the operator cabinet. Presentational: it renders the supplied
 * {@link ReportChatMessage}[] and emits {@link send}; all transport, polling
 * and optimistic bookkeeping live in the host.
 *
 * Visual contract mirrors the "Чат поддержки" frames of the `Репорты` Figma
 * file: dialogue (bubbles, sent-file chips, unread divider) + Text Area composer.
 */
@Component({
  selector: 'laboratory-report-chat-thread',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    TextFieldModule,
    LaboratoryAttachmentChipComponent,
    DsIconModule,
    DsSpinnerModule,
  ],
  templateUrl: './report-chat-thread.component.html',
  styleUrls: ['./report-chat-thread.component.scss'],
  viewProviders: [
    provideTranslationStatic(LABORATORY_REPORT_CHAT_TRANSLATION_SCHEME.tsScope, {
      de: deTranslation,
      en: enTranslation,
      es: esTranslation,
      fr: frTranslation,
      it: itTranslation,
      ja: jaTranslation,
      ko: koTranslation,
      pl: plTranslation,
      pt: ptTranslation,
      ru: ruTranslation,
      uk: ukTranslation,
    }),
  ],
})
export class ReportChatThreadComponent implements OnChanges, AfterViewInit {
  readonly ts = LABORATORY_REPORT_CHAT_TRANSLATION_SCHEME;

  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = toSignal(this.transloco.langChanges$, { initialValue: this.transloco.getActiveLang() });

  readonly messages = input<ReportChatMessage[]>([]);
  readonly viewerRole = input<ReportChatViewerRole>('student');
  readonly loading = input(false);
  readonly sending = input(false);
  /** True when the most recent send failed at the host level (drives the assertive alert). */
  readonly sendError = input(false);
  /** Composer hard-disable (e.g. report assigned to another operator). */
  readonly disabled = input(false);
  /**
   * Blocks only the send button while the rest of the composer stays live —
   * for hosts whose submit needs form fields outside the thread (report-form
   * category tabs / product picker) that are not filled in yet.
   */
  readonly sendDisabled = input(false);
  /** Tooltip explaining why the composer is disabled. */
  readonly disabledReason = input<string | null>(null);
  /** Optional override for the empty-state hint. Falls back to the localized default. */
  readonly slaText = input<string | null>(null);
  /** Optional title for a host-specific empty state, such as a new appeal. */
  readonly emptyTitleText = input<string | null>(null);
  /** Optional override for the composer placeholder. Falls back to the localized default. */
  readonly placeholderText = input<string | null>(null);
  /**
   * Placeholder of a DISABLED composer. Falls back to the generic «temporarily unavailable»
   * line, which is wrong for a lock that is final — a closed support dialog says so itself.
   * While the composer is locked it is also the field's accessible name.
   */
  readonly disabledPlaceholderText = input<string | null>(null);
  /** Id of the first unread message — renders the "Новые сообщения" divider before it. */
  readonly firstUnreadId = input<number | string | null>(null);
  /**
   * Compact time/date at the end of a bubble, full timestamp on hover.
   * Enabled across support surfaces; attachment-only messages keep an outside label.
   */
  readonly showTimestamps = input(true);
  /** When dates are grouped, individual message labels show only the time. */
  readonly showDateDividers = input(true);
  /** Shows the composer attach button. Off by default until the host wires uploads. */
  readonly allowAttachments = input(false);
  /** Attachment chips above the composer (draft, pre-send). */
  readonly draftAttachments = input<ReportChatDraftAttachment[]>([]);
  /** Pre-fills the composer (draft restore; showcase Filled/Long text states). */
  readonly draftText = input('');
  /** Shows the screenshot-capture button next to attach (modal compose view). */
  readonly allowScreenshot = input(false);
  /**
   * Render only the composer (no dialogue surface) — for hosts that embed
   * the Text Area as a plain submit form (lesson error reports) and for the
   * component-set stories.
   */
  readonly composerOnly = input(false);
  /**
   * Clear the composer after emitting {@link send}. Hosts that submit
   * asynchronously and keep the text as the retry payload (modal compose)
   * turn this off — on failure the message stays in place.
   */
  readonly clearOnSend = input(true);
  /**
   * When true (default), Enter submits and Shift+Enter inserts a newline. Hosts
   * where a stray Enter must NOT send — the error-report composer, where the
   * student writes a multi-line description — set this false so Enter always
   * inserts a newline and sending is button-only.
   */
  readonly sendOnEnter = input(true);
  /**
   * Showcase-only: forces the composer's :hover / :focus visuals so set
   * stories can render those states in a headless capture. Not for hosts.
   */
  readonly composerStateOverride = input<'hover' | 'focus' | null>(null);

  readonly send = output<string>();
  /** Re-emits a failed optimistic message for a retry. */
  readonly retry = output<ReportChatMessage>();
  /** Attach button pressed — the host opens its file picker. */
  readonly attachRequested = output<void>();
  /** Screenshot button pressed — the host runs its capture flow. */
  readonly screenshotRequested = output<void>();
  /**
   * Index of the draft attachment chip to remove, into {@link draftAttachments}.
   * The chip itself cannot address it: labels repeat (a clipboard/phone
   * screenshot is routinely `image.png`), and `draftChips` rebuilds fresh
   * objects on every read, so neither the name nor object identity survives as
   * a key. The index is the one thing the host and the chip list agree on.
   */
  readonly removeAttachment = output<number>();
  /** Mirrors the composer text for hosts that persist drafts. */
  readonly draftTextChange = output<string>();
  /** An inline message action button was pressed (host runs the behaviour). */
  readonly actionClicked = output<{ message: ReportChatMessage; actionId: string }>();
  /**
   * The highest message id the viewer has scrolled into view — everything up to
   * it has been seen. The host advances the read cursor to it (monotonic), so
   * reading is incremental ("up to where you scrolled"), not all-at-once: a
   * partial scroll marks only what actually reached the viewport. Emitted on
   * every scroll settle, the jump pill, and a new message arriving while pinned.
   */
  readonly viewedUpTo = output<number>();

  readonly textControl = new FormControl('', { nonNullable: true });

  private readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');
  private readonly textareaRef = viewChild<ElementRef<HTMLTextAreaElement>>('textarea');
  private readonly composerAutosize = viewChild(CdkTextareaAutosize);
  private readonly lightboxRef = viewChild<ElementRef<HTMLElement>>('lightbox');

  /** True when new messages arrived while the user was scrolled up reading history. */
  readonly hasUnreadBelow = signal(false);

  /**
   * Whether the viewport was at the bottom as of the last scroll event — the
   * "follow new messages" state. Sampled on scroll (BEFORE the next batch appends
   * rows), because measuring it inside {@link onMessagesChanged} is too late: the
   * new rows are already in the DOM but `scrollTop` has not moved, so a fresh
   * measurement reads "not at bottom" and catch-up (delivered in per-frame
   * batches) would strand the thread at the top. Starts true so the first paint
   * follows down.
   */
  private pinnedToBottom = true;

  /**
   * True once the viewer has genuinely interacted with the log (wheel / touch /
   * keyboard) — see {@link onUserScrollIntent}. The read cursor advances ONLY
   * after this: opening, parking at the divider, catch-up auto-scroll and layout
   * reflow all fire `scroll` events, but none of them are the user reading, so
   * they must not mark messages seen. Reset per session (empty message list).
   */
  private userHasScrolled = false;

  /**
   * True once the open-anchor scroll (park at the "new messages" divider, else
   * bottom) has run for the current session. Gates the follow-on auto-scroll so
   * it neither fights the anchor nor prematurely marks the thread read.
   */
  private opened = false;

  /** Enables native textarea scrolling only after autosize reaches its visual cap. */
  readonly composerScrollable = signal(false);

  readonly maxLength = MESSAGE_MAX_LENGTH;

  /**
   * Touch / pen devices cannot type Shift+Enter, so Enter must insert a
   * newline and the Send button becomes the only submit. Resolved once from
   * the pointer media query; defaults to fine (Enter = send) when matchMedia
   * is unavailable (SSR / jsdom).
   */
  readonly isCoarsePointer = signal(this.resolveCoarsePointer());

  private readonly today = signal(new Date());
  readonly rows = computed<ChatRow[]>(() =>
    this.buildRows(this.messages(), this.firstUnreadId(), this.today(), this.showDateDividers(), this.locale()),
  );

  readonly isEmpty = computed(() => this.messages().length === 0);

  /**
   * Draft chips with the image preview URL pre-sanitized: object URLs
   * (`blob:`) fail Angular's default `[src]` sanitizer, so they must go
   * through `bypassSecurityTrustUrl` (the host owns the URL lifecycle).
   */
  readonly draftChips = computed(() =>
    this.draftAttachments().map(attachment => ({
      source: attachment,
      safePreviewUrl: attachment.previewUrl ? this.sanitizer.bypassSecurityTrustUrl(attachment.previewUrl) : null,
    })),
  );

  /** Attachment currently open in the fullscreen lightbox (null = closed). */
  readonly previewedAttachment = signal<{ label: string; safePreviewUrl: SafeUrl } | null>(null);

  constructor() {
    this.subscribeOnVisibilityRead();
    interval(60_000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const now = new Date();
        if (now.toDateString() !== this.today().toDateString()) {
          this.today.set(now);
        }
      });

    // Drive the reactive control's enabled state from the `disabled` input
    // instead of `[attr.disabled]` — mixing the attribute with a reactive
    // FormControl triggers Angular's "disabled attribute with a reactive form"
    // warning and desyncs the control.
    effect(() => {
      if (this.disabled()) {
        this.textControl.disable({ emitEvent: false });
      } else {
        this.textControl.enable({ emitEvent: false });
      }
    });
  }

  ngAfterViewInit(): void {
    // `cdkTextareaAutosize` measures the textarea's `scrollHeight` in its own
    // `ngAfterViewInit`, but when the thread mounts inside a dialog that is still
    // being positioned/laid out the first measurement is stale and the composer
    // opens over-tall (a stray window resize later fixed it). Re-measure once the
    // layout has settled (next frame) so the composer is the right height on open.
    this.raf(() => this.composerAutosize()?.resizeToFitContent(true));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The read anchor for this session arrived (host computed it from the read
    // cursor) — or changed on a report switch: park at the "new messages"
    // divider, else at the bottom. Owns the initial scroll (opened flag).
    // Handled BEFORE messages so, on the batch that first reveals the divider,
    // parking clears the "follow bottom" state before onMessagesChanged reads it
    // — otherwise a stale pinned-to-bottom would scroll past the divider and
    // mark the unread messages read.
    if (changes.firstUnreadId) {
      this.scrollToOpenAnchor();
    }

    if (changes.messages) {
      this.onMessagesChanged(changes.messages.previousValue as ReportChatMessage[] | undefined);
    }

    if (changes.draftText && this.draftText() !== this.textControl.value) {
      this.textControl.setValue(this.draftText(), { emitEvent: false });
      // Programmatic fill bypasses the (input) handler — re-derive the
      // scrollable state once the view exists and autosize has grown the
      // textarea (on the first change the viewChild is not resolved yet).
      this.raf(() => {
        const textarea = this.textareaRef()?.nativeElement;
        if (textarea) {
          this.updateComposerScrollState(textarea);
        }
      });
    }
  }

  trackRow(_index: number, row: ChatRow): string {
    return row.key;
  }

  /** Thumbnail click — open the attachment image in the fullscreen lightbox. */
  openAttachmentPreview(chip: { source: ReportChatDraftAttachment; safePreviewUrl: SafeUrl | null }): void {
    if (chip.safePreviewUrl) {
      this.previewedAttachment.set({ label: chip.source.label, safePreviewUrl: chip.safePreviewUrl });
      // Focus the overlay so Escape closes it without an extra click.
      this.raf(() => this.lightboxRef()?.nativeElement.focus());
    }
  }

  /**
   * Files rendered under a bubble. A message sent with attachments carries the
   * list (`files`); hosts that synthesize a one-file row (the report prefix)
   * still set the single `file`, so both shapes render the same way.
   */
  attachmentsOf(message: ReportChatMessage): ReportChatMessageFile[] {
    return message.files ?? (message.file ? [message.file] : []);
  }

  /**
   * Click on a sent image attachment — preview it in the lightbox rather than
   * letting the browser download it (the storage URL serves
   * `Content-Disposition: attachment`, so a plain link would download an image
   * the user just wants to look at). Non-image attachments render as a download
   * link instead (see the template branch).
   */
  openMessageImage(file: { label: string; previewUrl?: string }): void {
    if (!file.previewUrl) {
      return;
    }
    this.previewedAttachment.set({
      label: file.label,
      safePreviewUrl: this.sanitizer.bypassSecurityTrustUrl(file.previewUrl),
    });
    this.raf(() => this.lightboxRef()?.nativeElement.focus());
  }

  closeAttachmentPreview(): void {
    this.previewedAttachment.set(null);
  }

  /**
   * Accessible author name for a message bubble (visually the design carries
   * authorship through alignment/colour only).
   *
   * Student viewer: the operator is never identified — every non-own message
   * collapses to the generic support label (privacy requirement).
   *
   * Staff viewer: own outbound is "You", a student message is labelled
   * "Student", another operator's message stays the generic support label —
   * still no id/name leak, but the student↔staff sides stay distinguishable.
   */
  authorLabel(message: ReportChatMessage): string {
    if (message.mine) {
      return this.ts.you;
    }
    if (this.viewerRole() === 'staff' && message.authorRole === 'student') {
      return this.ts.student;
    }
    return this.ts.supportName;
  }

  /**
   * Which side of the thread a bubble sits on (alignment + colour).
   *
   * Student viewer: personal — own messages right, everything else left.
   *
   * Staff viewer: by role — every staff message sits on the operator side
   * regardless of which operator authored it. `mine` alone would collapse a
   * thread handled by another operator into uniform "theirs" bubbles, making
   * the student's messages indistinguishable from support replies.
   */
  ownSide(message: ReportChatMessage): boolean {
    if (this.viewerRole() === 'staff') {
      return message.authorRole === 'staff';
    }
    return message.mine;
  }

  onTextareaKeydown(event: KeyboardEvent): void {
    // Send-by-button-only hosts: Enter always inserts a newline.
    if (!this.sendOnEnter()) {
      return;
    }
    // Coarse pointer (virtual keyboard): Enter always inserts a newline.
    if (this.isCoarsePointer()) {
      return;
    }
    if (event.shiftKey) {
      return;
    }
    event.preventDefault();
    this.submit();
  }

  submit(): void {
    if (this.disabled() || this.sendDisabled() || this.sending()) {
      return;
    }
    const body = this.textControl.value.trim();
    // An attachment alone is a valid submission (screenshot-only report).
    if ((!body && this.draftAttachments().length === 0) || body.length > this.maxLength) {
      return;
    }
    this.send.emit(body);
    if (this.clearOnSend()) {
      this.textControl.setValue('');
      this.draftTextChange.emit('');
      this.composerScrollable.set(false);
    }
  }

  onComposerInput(textarea: HTMLTextAreaElement): void {
    this.scheduleComposerScrollStateUpdate(textarea);
    this.draftTextChange.emit(this.textControl.value);
  }

  scheduleComposerScrollStateUpdate(textarea: HTMLTextAreaElement): void {
    this.raf(() => this.updateComposerScrollState(textarea));
  }

  private updateComposerScrollState(textarea: HTMLTextAreaElement): void {
    this.composerScrollable.set(textarea.scrollHeight > textarea.clientHeight + 1);
  }

  onRetry(message: ReportChatMessage): void {
    this.retry.emit(message);
  }

  /**
   * The viewer actually drove the scroll (wheel / touch drag / keyboard). This —
   * not the `scroll` event, which also fires for programmatic and reflow scrolls
   * — is what unlocks read-cursor advancement. Before it, onScroll is inert, so
   * the open anchor / catch-up auto-scroll never marks unread messages read.
   */
  onUserScrollIntent(): void {
    this.userHasScrolled = true;
  }

  onScroll(): void {
    // Ignore every scroll event until the viewer has genuinely interacted. The
    // open anchor (park / bottom), catch-up auto-scroll and layout reflow all
    // fire scroll events; treating them as reading is what cleared the dot on
    // open and yanked the parked divider to the bottom.
    if (!this.userHasScrolled) {
      return;
    }
    // Sample the follow-state on every user settle — this is the reading the next
    // message batch relies on (see pinnedToBottom): at the bottom → keep
    // following, scrolled up → stop yanking and surface the pill instead.
    this.pinnedToBottom = this.isPinnedToBottom();
    if (this.pinnedToBottom) {
      this.hasUnreadBelow.set(false);
    }
    // Advance the read cursor to the newest message now scrolled into view.
    this.emitViewedUpTo();
  }

  jumpToBottom(): void {
    // Tapping the pill IS a deliberate "catch me up" — mark the thread read.
    this.userHasScrolled = true;
    this.scrollToBottom();
    this.hasUnreadBelow.set(false);
    this.emitViewedUpToNow();
  }

  private onMessagesChanged(previous: ReportChatMessage[] | undefined): void {
    if (this.messages().length === 0) {
      // Report switch / fresh session (the host clears the list on reconnect) —
      // re-arm the open anchor and the read gating for the next thread.
      this.opened = false;
      this.userHasScrolled = false;
      this.pinnedToBottom = true;
      return;
    }
    const grew = (previous?.length ?? 0) < this.messages().length;
    // requestAnimationFrame so the DOM has the new rows before we measure.
    this.raf(() => {
      // The open-anchor owns the first scroll. An empty thread has nothing to
      // anchor on, so it only settles once the first message batch lands — retry
      // it here until it does (park at the divider, else land at the bottom).
      if (!this.opened) {
        this.scrollToOpenAnchor();
        return;
      }
      // Follow-state sampled BEFORE these rows appended (onScroll) — not remeasured
      // here, where the un-moved scrollTop would falsely read "not at bottom".
      if (this.pinnedToBottom) {
        this.scrollToBottom();
        this.hasUnreadBelow.set(false);
        // A new message arrived while pinned to the bottom — it landed on screen,
        // so it is seen without any scrolling to do. Only a hidden tab is not
        // reading: there the mark waits for the viewer to come back (see
        // subscribeOnVisibilityRead).
        this.emitViewedUpToWhenVisible();
        return;
      }
      if (grew) {
        this.hasUnreadBelow.set(true);
      }
    });
  }

  /**
   * The session's first scroll: park at the "new messages" divider when there is
   * one (read downward from there — nothing is marked read yet), otherwise land at
   * the bottom and report it seen. Runs on the read anchor arriving / changing.
   */
  private scrollToOpenAnchor(): void {
    this.raf(() => {
      // Nothing to anchor on yet — wait for the first message batch. The open
      // scroll must run on a populated thread, or it lands on an empty box and
      // the later-arriving rows leave it stuck at the top (never at the bottom).
      if (this.messages().length === 0) {
        return;
      }
      this.opened = true;
      if (this.firstUnreadId() != null && this.scrollDividerIntoView()) {
        if (this.threadFitsViewport()) {
          // The whole thread is on screen with nothing to scroll — the unread
          // messages ARE shown, so requiring a scroll would strand them unread
          // forever. Landing here is the reading.
          this.pinnedToBottom = true;
          this.emitViewedUpToNow();
          return;
        }
        // Parked at the divider — NOT at the bottom, so live rows must not yank
        // the viewport down while the user reads (surface the pill instead).
        this.pinnedToBottom = false;
        // Do NOT mark anything read yet. Opening is not reading; the cursor
        // advances only as the user scrolls the new messages into view (see
        // onScroll / onUserScrollIntent).
        return;
      }
      this.scrollToBottom();
      // Nothing unread → landed at the bottom → the whole thread is seen. This is
      // the one open-time mark: it only runs when there is no divider (cursor is
      // already at the latest), so it cannot mask an unread message.
      this.emitViewedUpToNow();
    });
  }

  /** Centre the "new messages" divider in the viewport so the last read message
   * and the first unread both show around it. Returns false when there is no
   * divider to scroll to. Uses rect deltas — robust to the divider's offset parent
   * (a plain `offsetTop` is relative to the wrong ancestor and lands at the top). */
  private scrollDividerIntoView(): boolean {
    const container = this.scrollContainer()?.nativeElement;
    const divider = container?.querySelector<HTMLElement>('.chat__divider');
    if (!container || !divider) {
      return false;
    }
    const containerRect = container.getBoundingClientRect();
    const dividerRect = divider.getBoundingClientRect();
    const delta = dividerRect.top - containerRect.top - (container.clientHeight - dividerRect.height) / 2;
    // eslint-disable-next-line functional/immutable-data
    container.scrollTop += delta;
    return true;
  }

  /**
   * Advance the read cursor to the newest message in view — but only once the
   * viewer has genuinely scrolled. Auto-scroll (open anchor, catch-up follow)
   * calls this too; the gate keeps opening from marking unread messages read.
   */
  private emitViewedUpTo(): void {
    if (!this.userHasScrolled) {
      return;
    }
    this.emitViewedUpToNow();
  }

  /**
   * Advance the read cursor for a message that arrived on screen by itself (live
   * row appended while pinned to the bottom) — no scroll is possible, so the
   * scroll gate cannot apply. Held back while the tab is hidden: a message that
   * landed with nobody looking has not been read; {@link subscribeOnVisibilityRead}
   * marks it once the viewer returns.
   */
  private emitViewedUpToWhenVisible(): void {
    if (this.document.visibilityState === 'hidden') {
      return;
    }
    this.emitViewedUpToNow();
  }

  /**
   * Mark what is on screen read when the viewer comes back to a tab that was
   * hidden while replies arrived. Only while pinned to the bottom — a viewer
   * parked up in history still reads by scrolling.
   */
  private subscribeOnVisibilityRead(): void {
    fromEvent(this.document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.document.visibilityState !== 'hidden' && this.opened && this.pinnedToBottom) {
          this.emitViewedUpToNow();
        }
      });
  }

  /**
   * Whether the log has nothing to scroll — the entire thread fits the viewport.
   * There is then no scrolling left to prove reading with, so what is rendered is
   * what is seen. Requires a measured viewport: without layout (clientHeight 0)
   * nothing has been shown yet and the scroll gate stays in charge.
   */
  private threadFitsViewport(): boolean {
    const el = this.scrollContainer()?.nativeElement;
    if (!el || el.clientHeight === 0) {
      return false;
    }
    return el.scrollHeight - el.clientHeight <= VIEWED_SLACK_PX;
  }

  /**
   * Emit the newest real (numeric-id) message id in view, unconditionally. For the
   * deliberate read points — landing at the bottom of an all-read thread on open,
   * and the jump-to-bottom pill — where the gate above does not apply.
   */
  private emitViewedUpToNow(): void {
    const id = this.computeViewedUpTo();
    if (id !== null) {
      this.viewedUpTo.emit(id);
    }
  }

  /**
   * The highest real (numeric-id) message whose bottom edge is at or above the
   * scroll viewport's bottom — i.e. fully scrolled into view ("seen"). Synthesized
   * report-prefix rows carry string ids and are skipped. Null when none is visible.
   */
  private computeViewedUpTo(): number | null {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) {
      return null;
    }
    const viewportBottom = container.getBoundingClientRect().bottom + VIEWED_SLACK_PX;
    let viewed: number | null = null;
    for (const row of Array.from(container.querySelectorAll<HTMLElement>('.chat__msg'))) {
      const id = Number(row.dataset.msgId);
      if (Number.isFinite(id) && row.getBoundingClientRect().bottom <= viewportBottom) {
        viewed = viewed === null ? id : Math.max(viewed, id);
      }
    }
    return viewed;
  }

  private isPinnedToBottom(): boolean {
    const el = this.scrollContainer()?.nativeElement;
    if (!el) {
      // No scroll context yet — treat as bottom so the first paint pins down.
      return true;
    }
    return el.scrollHeight - el.scrollTop - el.clientHeight <= PIN_TO_BOTTOM_SLACK_PX;
  }

  private scrollToBottom(): void {
    const el = this.scrollContainer()?.nativeElement;
    if (!el) {
      return;
    }
    // eslint-disable-next-line functional/immutable-data
    el.scrollTop = el.scrollHeight;
    // At the bottom now → keep following the next batch/live message down.
    this.pinnedToBottom = true;
  }

  private buildRows(
    messages: ReportChatMessage[],
    firstUnreadId: number | string | null,
    today: Date,
    groupDates: boolean,
    locale: string,
  ): ChatRow[] {
    let previousDay: string | null = null;
    return messages.flatMap(message => {
      const timeFormat = messageTimeFormat(message.createdAt, today);
      const messageRow: ChatRow = {
        kind: 'message',
        key: `msg-${message.id}`,
        message,
        // A system notice renders `systemText`, never `body` — parsing it would
        // tokenize a payload the template never shows, on every socket frame.
        bodySegments: message.body && !message.systemText ? splitBodyIntoSegments(message.body) : undefined,
        timeFormat: timeFormat && groupDates ? 'HH:mm' : timeFormat,
      };
      const day =
        groupDates && (message.authorRole !== 'system' || message.systemText)
          ? messageDay(message.createdAt, today)
          : null;
      const isNewDay = day && day.key !== previousDay;
      const dayRows: ChatRow[] = isNewDay
        ? [
            {
              kind: 'day',
              key: `day-${day.key}-${message.id}`,
              dayDate: day.key,
              dayLabelKey:
                day.relative === 'today' ? this.ts.today : day.relative === 'yesterday' ? this.ts.yesterday : undefined,
              dayLabel: new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'long',
                ...(day.date.getFullYear() !== today.getFullYear() ? ({ year: 'numeric' } as const) : {}),
              }).format(day.date),
            },
          ]
        : [];
      if (day) {
        previousDay = day.key;
      }
      if (firstUnreadId !== null && message.id === firstUnreadId) {
        const dividerRow: ChatRow = { kind: 'divider', key: `divider-${message.id}` };
        return [...dayRows, dividerRow, messageRow];
      }
      return [...dayRows, messageRow];
    });
  }

  private resolveCoarsePointer(): boolean {
    try {
      return this.document.defaultView?.matchMedia?.('(pointer: coarse)')?.matches === true;
    } catch {
      return false;
    }
  }

  private raf(cb: () => void): void {
    const view = this.document.defaultView;
    if (view?.requestAnimationFrame) {
      view.requestAnimationFrame(cb);
      return;
    }
    cb();
  }
}
